import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import ApplicationContext from '../../../ApplicationContext';
import { ApiResult, InsertResultsWithData } from '../../../framework.core/data/ApiResult';
import { getTbNameByJobName, JobNames } from '../../../framework.core/JobNames';
import useDirtyCheck from '../../../hooks/useDirtyCheck2';

import HblMilestoneView from './implementation/HblMilestone';

import { useTypedFastStorage } from '../../../hooks/useFastStorage';

import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';
import { SQLField, SQLInfo } from '../../../framework.core/data/SQLInfo';
import { historyUpdate } from '../../../fms.data/modules/FmsModFunc';
import { TrackableEntity } from '../../../fms.core/data/ChangeTrackable';
import { checkPropDirty, getSQLInfoList2 } from '../../../fms.core/data/TrackingModule';
import { HblStatusViewModel } from '../../../fms.core/HblStatusViewModel';
import SecondaryButton from '../../../ui/SecondaryButton';
import { stringsEqualIgnoreCase } from '../../../util';
import JobListViewContext from '../../common/JobListViewContext';
import fastStorage from "../../../fastStorage";
import { BlEventName } from '../../../framework.core/BlEventName';
import { BlEventLog } from '../../../fms.core/BlEventLog';
import { getFmsGlobal } from '../../../globals';
import constants from '../../../constants';
import useSetEditedId from '../../../hooks/useSetEditedId';

function HblBlStatusImpl({
  hblNo,
  hblId,
  storageKey,
  onClose,
  isView,
}: {
  hblNo: string,
  hblId: number,
  storageKey: string,
  onClose: (x:HblStatusViewModel|null) => void,
  isView: boolean,
}) {
  const appContext = useContext(ApplicationContext);
  const jobListContext = useContext(JobListViewContext);
  
  const [showGlobalOkDialog] = useGlobalOkDialog();

  const [initialized, setInitialized] = useState(false);
  
  const [blStatus, setBlStatus, removeBlStatus, blStatusRef] = useTypedFastStorage<HblStatusViewModel>(storageKey, null);
  const {setCleanState, dirtyCleanup} = useDirtyCheck(
    storageKey,
    ['updated', 'updatedBy']);

  const setSavedEntity = useSetEditedId([JobNames.OIHbl]);
  

  useEffect(() => {
    initialize();
    return () => {
      removeBlStatus();
      dirtyCleanup();
    }
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  function doClose(notifyResult: boolean = false) {
    onClose(notifyResult ? blStatusRef.current : null);
  }

  function refreshView(hblId: number) {
    // let keyMatch = _.keys(fastStorage).find(x => x.startsWith('ifms-list-data-/common/joblistview/hbl/oceanImport'));
    // if (keyMatch) {
    //   const listData = _.values(fastStorage[keyMatch]);
    //   // listData.filter(x => x.id === hblId)[0].anPrtChk = !!blStatusRef.current?.anPrtDate;
    //   // listData.filter(x => x.id === hblId)[0].cClear = !!blStatusRef.current?.cClearDate;
    //   // listData.filter(x => x.id === hblId)[0].cHold = !!blStatusRef.current?.cHoldDate;
    //   // jobListContext.updateDataRow(listData.filter(x => x.id === hblId)[0]);
    // }

    // OIH List
    setSavedEntity(hblId.toString(), false);

    // My Event List
    const keyMatch = _.keys(fastStorage).find(x => x.startsWith('ifms-list-data-/common/joblistview/MYEVT/myEvent'));
    if (keyMatch) {
      const listData = _.values(fastStorage[keyMatch]);
      listData.filter(x => x.hblId === hblId).forEach(row => {
        row.customsRlsDate = blStatusRef.current?.cReleaseDate;
        row.anSentDate = blStatusRef.current?.anPrtDate;
        jobListContext.updateDataRow(row);
      });
    }
  }

  async function initialize() {
    const apiResult:ApiResult<HblStatusViewModel> = await appContext.api.getHblStatusViewModel(JobNames.OIHbl, hblId);

    if (!apiResult) {
      await showGlobalOkDialog('Cannot load data');
      doClose();
      return;
    }

    const loadedModelObj = new HblStatusViewModel(apiResult.result);
    setCleanState(loadedModelObj);

    setBlStatus(new HblStatusViewModel(_.cloneDeep(loadedModelObj)));

    if(blStatusRef.current !== null) {
      blStatusRef.current.trackable = true;
    }

    setInitialized(true);
  }

  async function save() {
    if (!blStatus || !blStatusRef.current) {
      throw new Error('Invalid operation');
    }

    if(!blStatusRef.current.isDirty) {
      //await showGlobalOkDialog(<p>Nothing to save!!!</p>);
      doClose();
      return;
    }

    const si:SQLInfo[] = [];

    let hiSqlInfo = historyUpdate(getTbNameByJobName(JobNames.OIHbl), hblId, appContext.user?.id??'', 'Update B/L Info');
    if (hiSqlInfo) {
      si.push(hiSqlInfo);
    }
    
    const sqlInfoList = getSQLInfoList2(blStatusRef.current as TrackableEntity, HblStatusViewModel.getFieldMappings(JobNames.OIHbl));
    sqlInfoList.push(...si);

    sqlInfoList.filter(x => stringsEqualIgnoreCase(x.tableName, "t_aotherinfo")).forEach(x => {
      SQLInfo.setWhereFieldValueByFieldName(x, 'f_tbid', hblId);
    });

    sqlInfoList.filter(x => stringsEqualIgnoreCase(x.tableName, "t_oihmain")).forEach(x => {
      if(!SQLInfo.hasWhereFieldOfPropertyWithValue(x, 'f_id', hblId)){
        x.whereFields.push(new SQLField({fieldName: 'f_id', value: hblId}));
      }
    });

    const apiResult:ApiResult<InsertResultsWithData> = await appContext.api.saveHblStatusViewModel(sqlInfoList);

    if (apiResult && apiResult.success) {
      await UpdateLogHblMilestone();    //[t49-webhook] LogMblMilestone. 이 기능은 TrackingService를 사용하지 않더라도 적용한다.
      await showGlobalOkDialog(<p>The data has been saved</p>);
      refreshView(hblId);
      doClose(true);  
    } else {
      await showGlobalOkDialog(<div>
        <p>Cannot save</p>
        <p>{apiResult?.message}</p>
      </div>);
    }
  }
  
  async function UpdateLogHblMilestone() {
    if (!blStatus) return;

    let cmile:BlEventLog[] = [];

    if (checkPropDirty(blStatus, 'f3461Date')) {
      cmile.push(new BlEventLog(BlEventName.Fms3461, blStatus.f3461Date))
    }
    if (checkPropDirty(blStatus, 'f7501Date')) {
      cmile.push(new BlEventLog(BlEventName.Fms7501, blStatus.f7501Date))
    }
    if (checkPropDirty(blStatus, 'doSentDate')) {
      cmile.push(new BlEventLog(BlEventName.FmsDoSent, blStatus.doSentDate))
    }
    if (checkPropDirty(blStatus, 'delSchedDate')) {
      cmile.push(new BlEventLog(BlEventName.FmsDelSched, blStatus.delSchedDate))
    }
    if (checkPropDirty(blStatus, 'delActDate')) {
      cmile.push(new BlEventLog(BlEventName.FmsDelAct, blStatus.delActDate))
    }

    if (cmile.length > 0) {
      const tempData = cmile.sort((a, b) => new Date(a.eventAt ?? '').getTime() - new Date(b.eventAt ?? '').getTime());
      const lastEvents: BlEventLog = tempData[tempData.length - 1];
      lastEvents.suntekID = getFmsGlobal().AcmeID ?? '';
      lastEvents.jobName = JobNames.OIHbl;
      lastEvents.jobId = hblId;
      lastEvents.blNo = blStatus.hblNo;
      //lastEvents.eventAtUtc
      lastEvents.u1Id = appContext.user?.id ?? '';
      const apiResult = await appContext.api.trackingLogHblMilestone(lastEvents);;
      if (!apiResult || !apiResult.success) {
        await showGlobalOkDialog(<p>{apiResult?.message ?? constants.LOAD_ERROR_MESSAGE}</p>);
      }
    }
  }

  return (
    <div>
      {!initialized &&
        <div style={{position: 'relative', top: '-25px'}}>
          <SkeletonTheme color="#4287f5" highlightColor="#b9d2fa">
            <Skeleton count={1} height={2} />
          </SkeletonTheme>
        </div>
      }

      {!!blStatus &&
        <div className='report-popup'>
          <HblBlStatusInitializedImpl hbl={blStatus} setHbl={setBlStatus} isView={isView} hblNo={hblNo} />

          <div className='report-footer'>
            <SecondaryButton
              name='oih-Milestone-update-button'
              size="regular"
              style={{width: '90px'}}
              onClick={save}>
              Update
            </SecondaryButton>
            <SecondaryButton
              name='oih-Milestone-cancel-button'
              size="regular"
              style={{width: '90px'}}
              onClick={doClose}>
              Cancel
            </SecondaryButton>
          </div>
        </div>
      }
    </div>
  );
}

function HblBlStatusInitializedImpl({
  hblNo,
  hbl,
  setHbl,
  isView,
}: {
  hblNo: string,
  hbl: HblStatusViewModel,
  setHbl: (x:Partial<HblStatusViewModel>) => void,
  isView: boolean,
}) {
  const appContext = useContext(ApplicationContext);

  return (
    <HblMilestoneView
      hblNo={hblNo}
      hbl={hbl}
      onChange={setHbl}
      isView={isView}
      isEdit={!isView}
      user={appContext.user?.id ?? ''}
    />
  );
}

const HblBlStatusPopup = ({
  hblNo,
  hblId,
  storageKey,
  onClose,
  isView,
}:{
  hblNo: string,
  hblId: number,
  storageKey: string,
  onClose:  (x:HblStatusViewModel|null) => void,
  isView: boolean,
}) => {
  return (
    <HblBlStatusImpl hblNo={hblNo} hblId={hblId} key={storageKey} storageKey={storageKey} onClose={onClose} isView={isView} />
  );
};

HblBlStatusPopup.defaultProps = {
};

HblBlStatusPopup.propTypes = {
  hblId: PropTypes.number.isRequired,
  storageKey: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  isView: PropTypes.bool.isRequired,
};

export default React.memo(HblBlStatusPopup);
